import React, { useEffect, useRef, useState } from "react";
import "./PostAssets.scss";
import { CommonHelpers } from "../../../../../utils/helpers/common.helpers";
import { CommonEnums } from "../../../../../enums/common.enums";
import CustomPageLoader from "../../../../../components/loaders/CustomPageLoader";
import { useParams, useLocation } from "react-router-dom";
import PreviewModal from "../../propertypost/view/PreviewModal";
import Upload from "../../../../../components/icons/Upload";
import PostAssetMenu from "./PostAssetMenu";
import { PropertyApi } from "../../../../../utils/api/Property";

interface DesignAsset {
  id: string;
  imageUrl: string;
  imageRelativePath: string;
  templateRelativePath: string;
  taskStatus?: string;
}

const PostAssets: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [designAssets, setDesignAssets] = useState<DesignAsset[]>([]);
  const { scheduleId } = useParams<{ scheduleId: string }>();
  const location = useLocation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewImageUrls, setPreviewImageUrls] = useState<string[]>([]);
  const [updatingAsset, setUpdatingAsset] = useState<DesignAsset | null>(null);
  const [updatedImageFile, setUpdatedImageFile] = useState<File | null>(null);
  const [updatedImagePreviewUrl, setUpdatedImagePreviewUrl] = useState<
    string | null
  >(null);
  const queryParams = new URLSearchParams(location.search);
  const postType = queryParams.get("post_type");

  const fetchPropertyPost = async () => {
    if (!scheduleId) return;
    try {
      setLoading(true);
      const res = await PropertyApi.getOpenHomeScheduleById(scheduleId);

      if (res?.data?.success) {
        const post = res.data.data;
        let designActivityRefs: any[] = [];

        if (postType === "story_post") {
          designActivityRefs = post.story_post_design_activity_ref
            ? [].concat(post.story_post_design_activity_ref)
            : [];
        } else if (postType === "video_post") {
          designActivityRefs = post.video_post_design_activity_ref
            ? [].concat(post.video_post_design_activity_ref)
            : [];
        } else if (postType === "reel_post") {
          designActivityRefs = post.reel_post_design_activity_ref
            ? [].concat(post.reel_post_design_activity_ref)
            : [];
        } else {
          designActivityRefs = post.image_post_design_activity_ref
            ? [].concat(post.image_post_design_activity_ref)
            : [];
        }

        const fetchedAssets = designActivityRefs.reduce<DesignAsset[]>(
          (accumulator, ref, index) => {
            const designTask = ref?.design_task_id;

            if (designTask) {
              accumulator.push({
                id: ref._id || `asset-${index}`,
                imageUrl: designTask.output_image_path
                  ? CommonHelpers.getImageUrlFromImageTool({
                      path: designTask.output_image_path,
                    })
                  : "",
                imageRelativePath: designTask.output_image_path || "N/A",
                templateRelativePath:
                  designTask.output_image_template_path || "N/A",
                taskStatus: designTask.task_status,
              });

              if (Array.isArray(designTask.child_design_tasks)) {
                designTask.child_design_tasks.forEach(
                  (childTask: any, childIndex: number) => {
                    accumulator.push({
                      id: `${ref._id || `asset-${index}`}-child-${childIndex}`,
                      imageUrl: childTask.output_image_path
                        ? CommonHelpers.getImageUrlFromImageTool({
                            path: childTask.output_image_path,
                          })
                        : "",
                      imageRelativePath: childTask.output_image_path || "N/A",
                      templateRelativePath:
                        childTask.output_image_template_path || "N/A",
                      taskStatus:
                        childTask.task_status || designTask.task_status,
                    });
                  }
                );
              }
            }

            return accumulator;
          },
          []
        );

        setDesignAssets(fetchedAssets);
      } else {
        setError("Failed to load property post");
      }
    } catch (err) {
      setError("Something went wrong while fetching");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (scheduleId) fetchPropertyPost();
  }, [scheduleId, postType]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setUpdatedImageFile(file);
      setUpdatedImagePreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleStartUpdate = (asset: DesignAsset) => {
    setUpdatingAsset(asset);
  };

  const handleCancelUpdate = () => {
    setUpdatingAsset(null);
    setUpdatedImageFile(null);
    setUpdatedImagePreviewUrl(null);
  };

  const handleUpdateImage = async () => {
    if (!updatedImageFile || !updatingAsset) return;
    console.log(
      `Uploading file for asset ${updatingAsset.id}:`,
      updatedImageFile
    );
    alert("Image updated successfully! (Simulation)");
    handleCancelUpdate();
    fetchPropertyPost();
  };

  useEffect(() => {
    return () => {
      if (updatedImagePreviewUrl) {
        URL.revokeObjectURL(updatedImagePreviewUrl);
      }
    };
  }, [updatedImagePreviewUrl]);

  const renderDesignImage = (item: any) => {
    const assetUrlRaw = item?.design_task_id?.output_image_path;
    const designTaskStatus = item?.design_task_id?.task_status;
    const isLoading = designTaskStatus === CommonEnums.taskStatus.queued;
    const isErrored = designTaskStatus === CommonEnums.taskStatus.errored;
    const assetUrl = assetUrlRaw
      ? CommonHelpers.getImageUrlFromImageTool({ path: assetUrlRaw })
      : null;

    if (isErrored) {
      return (
        <div className="activity-image-section image-placeholder-container">
          <img
            src="/images/image-video-generation-error-fallback-image-for-card.png"
            alt="Error Preview"
            className="activity-thumbnail placeholder-image"
          />
        </div>
      );
    }

    if (isLoading) {
      const fallback =
        postType === CommonEnums.postType.video_post.key
          ? "/images/square-fallback-image-for-video-preview.png"
          : "/images/square-fallback-image-for-image-preview.png";
      return (
        <div className="activity-image-section image-placeholder-container">
          <img
            src={fallback}
            alt="Loading Preview"
            className="activity-thumbnail placeholder-image"
          />
          <div className="loader-container">
            <CustomPageLoader className="activity-card-loader" />
            <p className="processing-text font-poppins-400">Processing...</p>
          </div>
        </div>
      );
    }

    let containerClass = "activity-image-section";
    if (postType === "story_post")
      containerClass += " story-post-image-container";
    if (postType === "video_post")
      containerClass += " video-placeholder-collection";
    if (postType === "reel_post")
      containerClass += " reel-placeholder-collection";

    return (
      <div className={containerClass}>
        <img
          src={
            assetUrl ||
            "/images/image-video-generation-error-fallback-image-for-card.png"
          }
          alt="Asset Preview"
          className="activity-thumbnail"
          onError={(e: any) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src =
              "/images/image-video-generation-error-fallback-image-for-card.png";
          }}
        />
      </div>
    );
  };

  if (loading)
    return (
      <div className="post-assets-loader">
        <CustomPageLoader />
      </div>
    );
  if (error)
    return (
      <div className="post-assets-error">
        <p>{error}</p>
      </div>
    );

  const handlePreview = (imageUrl: string) => {
    setPreviewImageUrls([imageUrl]);
    setIsPreviewOpen(true);
  };

  return (
    <div className="post-assets-container">
      <div className="section">
        <h2 className="section-title">Output Images For the Post</h2>
        <p className="section-subtitle">
          Post images generated from the design automation tool
        </p>

        <div className="assets-grid">
          {designAssets.map((asset) => (
            <div key={asset.id} className="asset-card">
              {updatingAsset?.id === asset.id ? (
                <div className="update-image-container">
                  <div className="update-header-wrapper">
                    <button
                      className="cancel-update-home"
                      onClick={handleCancelUpdate}
                    >
                      Cancel
                    </button>
                  </div>
                  {updatedImagePreviewUrl ? (
                    <div className="image-preview-container">
                      <img
                        src={updatedImagePreviewUrl}
                        alt="New preview"
                        className="preview-image"
                      />
                    </div>
                  ) : (
                    <>
                      <div
                        className="file-uploader-box"
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <Upload />
                        <p className="uploader-text">Drop or Select file</p>
                      </div>
                      <input
                        type="file"
                        ref={fileInputRef}
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                        accept="image/*"
                      />
                    </>
                  )}
                </div>
              ) : (
                <div className="image-container-with-menu">
                  {renderDesignImage({
                    _id: asset.id,
                    design_task_id: {
                      output_image_path: asset.imageRelativePath,
                      task_status: asset.taskStatus,
                    },
                  })}
                  <div className="activity-options-menu">
                    <PostAssetMenu
                      previewImageUrl={asset.imageUrl}
                      templateRelativePath={asset.templateRelativePath}
                      onPreview={() => handlePreview(asset.imageUrl)}
                      onUpdate={() => handleStartUpdate(asset)}
                    />
                  </div>
                </div>
              )}

              <h4 className="relative-path-title">
                Image file relative path on design tool
              </h4>
              <p className="relative-path">{asset.imageRelativePath}</p>

              <h4 className="relative-path-title">
                Template file relative path on design tool
              </h4>
              <p className="relative-path">{asset.templateRelativePath}</p>
            </div>
          ))}
        </div>
      </div>

      <PreviewModal
        open={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        images={previewImageUrls}
      />
    </div>
  );
};

export default PostAssets;
